import {
  LeaveAbsenceColor,
  type LeaveAbsenceColor as Color,
  type LeaveDayPartCode,
} from '@gb-ticket/shared-types';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';
import { LEAVE_REQUEST_STATUS_LABEL } from '@/products/leave/features/leave-labels';
import { fillClassForStatus } from '@/products/leave/features/leave-type-colors';

export type LeaveDayCellSize = 'day' | 'chip';

export type LeaveDayCellProps = {
  size?: LeaveDayCellSize;
  dayNumber?: number | null;
  color?: Color;
  mark?: string | null;
  /** Second mark when two absences share a day. */
  extraMark?: string | null;
  /** When more than two absences, show a trailing +. */
  moreMarks?: boolean;
  /** When null, the cell is empty (weekend / holiday without absence). */
  status?: 'approved' | 'submitted' | null;
  part?: LeaveDayPartCode;
  holiday?: boolean;
  holidayName?: string | null;
  schoolHoliday?: boolean;
  schoolHolidayName?: string | null;
  weekend?: boolean;
  today?: boolean;
  /** German type label for the title. Already masked by the calendar payload. */
  labelDe?: string | null;
  /** Own request detail path — weekends and empty days stay non-interactive. */
  href?: string | null;
  /** Range pick. Ignored when href is set. */
  onSelect?: () => void;
  selected?: boolean;
  className?: string;
};

export function LeaveDayCell({
  size = 'day',
  dayNumber = null,
  color = LeaveAbsenceColor.SLATE,
  mark = null,
  extraMark = null,
  moreMarks = false,
  status = null,
  part = 'full',
  holiday = false,
  holidayName = null,
  schoolHoliday = false,
  schoolHolidayName = null,
  weekend = false,
  today = false,
  labelDe = null,
  href = null,
  onSelect,
  selected = false,
  className,
}: LeaveDayCellProps) {
  const chip = size === 'chip';
  const hasAbsence = status != null;
  const joinedMarks = [mark, extraMark].filter((value): value is string => Boolean(value)).join('');
  const displayMark =
    joinedMarks.length > 0 || moreMarks ? `${joinedMarks}${moreMarks ? '+' : ''}` : null;
  const title = cellTitle({
    dayNumber,
    labelDe,
    status,
    holidayName,
    schoolHolidayName,
    mark: displayMark,
  });

  const classes = cn(
    'relative overflow-hidden border text-center leading-none',
    chip
      ? 'inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-[10px] font-semibold'
      : 'flex size-10 min-h-10 min-w-10 flex-col items-stretch rounded-sm text-xs',
    schoolHoliday && !hasAbsence ? 'bg-orange-100 dark:bg-orange-950/50' : null,
    weekend && !hasAbsence && !schoolHoliday ? 'bg-muted' : null,
    !weekend && !schoolHoliday && !hasAbsence ? 'bg-card' : null,
    holiday ? 'border-red-500' : 'border-border',
    today ? 'ring-1 ring-primary' : null,
    selected ? 'ring-2 ring-primary ring-offset-1' : null,
    href || onSelect ? 'outline-none focus-visible:ring-2 focus-visible:ring-ring' : null,
    className,
  );

  const body = (
    <>
      {schoolHoliday && hasAbsence ? (
        <span
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-orange-400"
          aria-hidden
        />
      ) : null}
      {dayNumber != null && !chip ? (
        <span
          className={cn(
            'px-0.5 pt-0.5 text-[10px] font-semibold',
            hasAbsence ? 'text-inherit' : 'text-foreground',
          )}
        >
          {dayNumber}
        </span>
      ) : null}
      {hasAbsence ? (
        <AbsenceFill chip={chip} color={color} mark={displayMark} status={status} part={part} />
      ) : chip ? (
        <span className="text-muted-foreground" aria-hidden>
          ·
        </span>
      ) : null}
    </>
  );

  if (href) {
    return (
      <Link to={href} title={title} aria-label={title} className={classes}>
        {body}
      </Link>
    );
  }

  if (onSelect) {
    return (
      <button
        type="button"
        title={title}
        aria-label={title}
        aria-pressed={selected}
        className={classes}
        onClick={onSelect}
      >
        {body}
      </button>
    );
  }

  return (
    <div title={title} aria-label={title} className={classes}>
      {body}
    </div>
  );
}

function AbsenceFill({
  chip,
  color,
  mark,
  status,
  part,
}: {
  chip: boolean;
  color: Color;
  mark: string | null;
  status: 'approved' | 'submitted' | null;
  part: LeaveDayPartCode;
}) {
  const fill = fillClassForStatus(color, status ?? 'approved');
  if (part === 'full') {
    return (
      <span
        className={cn(
          'flex items-center justify-center font-semibold',
          chip ? 'size-full' : 'min-h-4 flex-1',
          fill,
        )}
      >
        {mark}
      </span>
    );
  }
  return (
    <span className={cn('flex flex-1', chip ? 'size-full' : 'min-h-4')}>
      <span
        className={cn(
          'flex w-1/2 items-center justify-center font-semibold',
          part === 'morning' ? fill : 'bg-transparent',
        )}
      >
        {part === 'morning' ? mark : null}
      </span>
      <span
        className={cn(
          'flex w-1/2 items-center justify-center font-semibold',
          part === 'afternoon' ? fill : 'bg-transparent',
        )}
      >
        {part === 'afternoon' ? mark : null}
      </span>
    </span>
  );
}

function cellTitle(input: {
  dayNumber: number | null;
  labelDe: string | null;
  status: 'approved' | 'submitted' | null;
  holidayName: string | null;
  schoolHolidayName: string | null;
  mark: string | null;
}): string {
  const parts: string[] = [];
  if (input.dayNumber != null) {
    parts.push(`Tag ${input.dayNumber}`);
  }
  if (input.labelDe) {
    parts.push(input.labelDe);
    if (input.mark) {
      parts.push(input.mark);
    }
    if (input.status === 'approved' || input.status === 'submitted') {
      parts.push(LEAVE_REQUEST_STATUS_LABEL[input.status]);
    }
  }
  if (input.holidayName) {
    parts.push(input.holidayName);
  }
  if (input.schoolHolidayName) {
    parts.push(input.schoolHolidayName);
  }
  return parts.join(' · ') || 'Tag';
}
